import { Text, TextSize, TextWeight } from '@vizit/atoms';
import { WarningCircleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import type * as React from 'react';

interface AdvisoryBannerProps {
  /**
   * Headline stating the caveat, e.g. "Not in this category's optimal mix".
   */
  title: string;
  /**
   * Supporting sentence explaining the consequence for the user.
   */
  description: React.ReactNode;
  /**
   * Optional trailing action, typically a "Learn more" link.
   *
   * @optional
   */
  action?: React.ReactNode;
  /**
   * Optional class name for the root element.
   *
   * @optional
   */
  className?: string;
}

/**
 * Advisory warning shown when a score carries a caveat the user should weigh — an asset class
 * outside the category's optimal mix, a hero scored without a product title, and so on. Purely
 * presentational: the caller owns both the copy and the decision of when to render it.
 *
 * @param props - See {@link AdvisoryBannerProps}.
 * @returns The rendered banner.
 */
function AdvisoryBanner(props: AdvisoryBannerProps) {
  const { title, description, action, className } = props;

  return (
    <div
      className={cx(
        'flex items-center gap-6 p-6 opacity-80',
        'bg-warning-25 border border-solid border-warning',
        'shadow-[0px_4px_6px_-1px_rgba(10,13,18,0.1),0px_2px_4px_-2px_rgba(10,13,18,0.06)]',
        className,
      )}
      role="note"
    >
      <WarningCircleIcon size={24} className="shrink-0 text-warning-700" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Text
          elementType="p"
          size={TextSize.Sm}
          weight={TextWeight.Semibold}
          className="text-warning-800"
          showEllipsis={false}
        >
          {title}
        </Text>
        <Text
          elementType="p"
          size={TextSize.Sm}
          weight={TextWeight.Medium}
          className="text-warning-800"
          showEllipsis={false}
        >
          {description}
        </Text>
        {action}
      </div>
    </div>
  );
}

export { AdvisoryBanner };
export type { AdvisoryBannerProps };
